import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Autocomplete,
  Stack,
  InputAdornment,
  Tooltip,
  Divider,
} from "@mui/material";
import AdminCsvUpload from "./adminCsvUpload";
import {
  fetchUnits,
  saveUnit,
  deleteUnit,
} from "../../Controllers/unitController";
import { fetchActivities } from "../../Controllers/activityController";
import { useError } from "../../Context/errorContext";
import GroupAddRounded from "@mui/icons-material/GroupAddRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReorderableList from "./reorderableList";

const AdminUnits = () => {
  const [units, setUnits] = useState([]);
  const [activities, setActivities] = useState([]);
  const [draft, setDraft] = useState({ name: "", email: "" });
  const [draftError, setDraftError] = useState("");
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const { setAlert } = useError();

  const getParentUnitCode = (parentUnitId) => {
    const parent = units.find((u) => u.unit_id === parentUnitId);
    return parent ? parent.unit_code : "";
  };

  useEffect(() => {
    fetchUnits(setUnits);
    fetchActivities(setActivities);
  }, []);

  const handleSave = async () => {
    saveUnit(selectedUnit, units, setIsEditing, setAlert, setUnits);
  };

  const handleDelete = async (id) => {
    deleteUnit(id, setUnits, setAlert);
  };

  const getParentUnitName = (parentId) => {
    const parent = units.find((u) => u.unit_id === parentId);
    return parent ? `${parent.unit_code} - ${parent.unit_name}` : "—";
  };

  const handleChange = async (items) => {
    setSelectedUnit((prev) => ({ ...prev, approvers: items }));
  };

  const approversEmailSet = useMemo(() => {
    return new Set(
      (Array.isArray(selectedUnit?.approvers)
        ? selectedUnit.approvers
        : []
      ).map((e) => String(e.email || "").toLowerCase()),
    );
  }, [selectedUnit]);

  const addDraft = () => {
    const approver_name = draft.name.trim();
    const approver_email = draft.email.trim();
    if (!approver_name || !approver_email) return;
    const key = approver_email.toLowerCase();
    console.log(approversEmailSet);
    if (approversEmailSet.has(key)) {
      setDraftError("A unit approver already has this email.");
      return;
    }
    setSelectedUnit((prev) => ({
      ...prev,
      approvers: [...selectedUnit.approvers, { approver_name, approver_email }],
    }));
    setDraft({ name: "", email: "" });
    setDraftError("");
  };

  return (
    <Paper sx={{ padding: 3, marginBottom: 4 }}>
      <Typography variant="h6">Units</Typography>
      <Box mt={2}>
        <Button
          variant="contained"
          onClick={() => {
            setSelectedUnit({
              unit_code: "",
              unit_name: "",
              unit_manager_name: "",
              unit_manager_email: "",
              approvers: [],
              activities: [],
              parent_unit_id: null,
            });
            setIsEditing(true);
          }}
        >
          Add Unit
        </Button>
      </Box>
      <Table sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Manager Name</TableCell>
            <TableCell>Manager Email</TableCell>
            <TableCell>Parent Unit</TableCell>
            <TableCell>Actions</TableCell>
            <TableCell>Approvers</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {units.map((unit) => (
            <TableRow key={unit.unit_id}>
              <TableCell>{unit.unit_code}</TableCell>
              <TableCell>{unit.unit_name}</TableCell>
              <TableCell>{unit.unit_manager_name}</TableCell>
              <TableCell>{unit.unit_manager_email}</TableCell>
              <TableCell>{getParentUnitName(unit.parent_unit_id)}</TableCell>
              <TableCell>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSelectedUnit({
                      ...unit,
                      approvers: unit.approvers,
                      parent_unit_code: getParentUnitCode(unit.parent_unit_id),
                    });
                    setIsEditing(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleDelete(unit.unit_id)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AdminCsvUpload
        refresh={fetchUnits}
        csvType={"unit"}
        data={units}
      ></AdminCsvUpload>

      <Dialog open={isEditing} onClose={() => setIsEditing(false)} fullWidth>
        <DialogTitle>
          {selectedUnit?.unit_id ? "Edit Unit" : "Add Unit"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Code"
            value={selectedUnit?.unit_code || ""}
            onChange={(e) =>
              setSelectedUnit({ ...selectedUnit, unit_code: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Name"
            value={selectedUnit?.unit_name || ""}
            onChange={(e) =>
              setSelectedUnit({ ...selectedUnit, unit_name: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Manager Name"
            value={selectedUnit?.unit_manager_name || ""}
            onChange={(e) => {
              setSelectedUnit({
                ...selectedUnit,
                unit_manager_name: e.target.value,
              });
            }}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Manager Email"
            value={selectedUnit?.unit_manager_email || ""}
            onChange={(e) => {
              setSelectedUnit({
                ...selectedUnit,
                unit_manager_email: e.target.value,
              });
            }}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Parent Unit (optional)</InputLabel>
            <Select
              value={selectedUnit?.parent_unit_code || ""}
              label="Parent Unit"
              onChange={(e) =>
                setSelectedUnit({
                  ...selectedUnit,
                  parent_unit_code: e.target.value || null,
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              {units
                .filter((u) => u.unit_id !== selectedUnit?.unit_id) // Exclude self
                .map((unit) => (
                  <MenuItem key={unit.unit_code} value={unit.unit_code}>
                    {unit.unit_code} - {unit.unit_name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            options={activities || []}
            disableCloseOnSelect
            fullWidth
            size="small"
            getOptionLabel={(o) => o.activity_name}
            isOptionEqualToValue={(o, v) => o.activity_id === v.activity_id}
            value={selectedUnit?.activities || []}
            onChange={(e, newValue) =>
              setSelectedUnit({
                ...selectedUnit,
                activities: newValue || null,
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                {option.activity_name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Activities"
                placeholder="Select activities"
                margin="normal"
              />
            )}
            // nice UX: show up to 3 chips, then “+N”
            limitTags={3}
          />

          <Stack spacing={2}>
            {/* Add approvers block */}
            <Stack direction="column" gap={1}>
              <Stack direction={"row"} gap={1}>
                <TextField
                  size="small"
                  label="Approver name"
                  fullWidth
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addDraft();
                  }}
                  sx={{ width: "40%" }}
                />
                <TextField
                  size="small"
                  label="Approver email"
                  type="email"
                  fullWidth
                  value={draft.email}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, email: e.target.value }));
                    if (draftError) setDraftError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addDraft();
                  }}
                  error={Boolean(draftError)}
                  InputProps={{
                    endAdornment: draftError ? (
                      <InputAdornment position="end">
                        <Tooltip title={draftError}>
                          <WarningAmberIcon color="warning" />
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{ width: "40%" }}
                />

                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<GroupAddRounded />}
                  onClick={addDraft}
                  disabled={draft.email === "" || draft.name === ""}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 9999,
                    px: 2.25,
                    py: 0.75,
                    minHeight: 40,
                    minWidth: { xs: "100%", sm: "auto" }, // same as above
                    width: { xs: "100%", sm: "25%" },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    borderColor: "rgb(209, 173, 115)",
                    color: "#041e41dc",
                    "&:hover": {
                      borderColor: "rgb(209, 173, 115)",
                      backgroundColor: "rgba(209, 173, 115, 0.12)",
                    },
                  }}
                >
                  Add approver
                </Button>
              </Stack>
            </Stack>
            <Divider />
            {/* Approvers list */}
            <Stack
              direction="column"
              alignItems="start"
              justifyContent="space-between"
              sx={{ px: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Approvers
              </Typography>
            </Stack>
            <ReorderableList
              items={selectedUnit?.approvers ?? []}
              onChange={handleChange}
            ></ReorderableList>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminUnits;
